import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAppDispatch, useAppSelector, useAuth } from '../hooks';

import { ExpenseClaimActions } from '../actions/expense-claim/expense-claim.actions';

import type { ExpenseClaim } from '../interfaces/employee/expense-claim.interface';

import Topbar from './components/Topbar';
import Icon from './components/Icon';
import FilterChipStrip from './components/FilterChipStrip';
import { formatDate, formatZar } from './lib/mandate-helpers';
import { useTableSort, SortableTh } from './lib/use-table-sort';

// =============================================================================
//  Expense claims — v3
//
//  Builds on the HR list idiom established by HrDocumentLocker and adds the
//  first approval control in v3.  Approve/deny is a status change on the
//  record; the API refuses it for anyone without an HR role
//  (reject_unprivileged_status_change!), so the buttons are gated to match.
//
//  Employees see only their own claims, so the Employee column is hidden for
//  them — it would repeat their own name on every row.
// =============================================================================

type StatusKey = 'All' | 'requested' | 'approved' | 'denied';

const STATUS_OPTIONS: ReadonlyArray<{ value: StatusKey; label: string }> = [
  { value: 'All', label: 'All' },
  { value: 'requested', label: 'Awaiting' },
  { value: 'approved', label: 'Approved' },
  { value: 'denied', label: 'Denied' }
];

const STATUS_TONE: Record<string, string> = {
  requested: 'warn',
  approved: 'success',
  denied: 'danger'
};

const STATUS_LABEL: Record<string, string> = {
  requested: 'Awaiting',
  approved: 'Approved',
  denied: 'Denied'
};

/** "12 Mar 2026" or a range when the claim spans days. */
const periodLabel = (c: ExpenseClaim): string => {
  const from = formatDate(c.date);
  const to = c.end_date ? formatDate(c.end_date) : from;
  return from === to ? from : `${from} → ${to}`;
};

const ExpenseClaimsListV3 = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { isHrPrivileged } = useAuth();

  const claims: ExpenseClaim[] =
    useAppSelector(({ expense_claim_store }) => expense_claim_store.expenseClaims) || [];

  const [loaded, setLoaded] = useState(claims.length > 0);
  const [statusFilter, setStatusFilter] = useState<StatusKey>('All');
  const [search, setSearch] = useState('');
  const [exporting, setExporting] = useState(false);
  // Id currently being approved/denied, so the row's buttons can disable.
  const [savingId, setSavingId] = useState<string | number | null>(null);

  const reload = () => dispatch(ExpenseClaimActions.loadExpenseClaims(() => setLoaded(true)));

  useEffect(() => {
    dispatch(ExpenseClaimActions.loadExpenseClaims(() => setLoaded(true)));
  }, [dispatch]);

  const handleExport = () => {
    if (exporting) return;
    setExporting(true);
    dispatch(ExpenseClaimActions.downloadExpenseClaims(() => setExporting(false)));
  };

  // Send the whole record back with the new status rather than a bare
  // {status} patch — the legacy update action expects a full payload.
  const setStatus = (claim: ExpenseClaim, status: 'approved' | 'denied') => {
    if (!claim.id || savingId) return;
    setSavingId(claim.id);
    dispatch(
      ExpenseClaimActions.updateExpenseClaim(claim.id, { ...claim, status } as ExpenseClaim, () => {
        setSavingId(null);
        reload();
      })
    );
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return claims.filter((c) => {
      if (statusFilter !== 'All' && (c.status || 'requested') !== statusFilter) return false;
      if (q) {
        const hay =
          `${c.employee ?? ''} ${c.client ?? ''} ${c.category ?? ''} ${c.reason ?? ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [claims, statusFilter, search]);

  // Newest first by default; every column is clickable.
  const SORT_ACCESSORS = useMemo(
    () => ({
      employee: (c: ExpenseClaim) => c.employee,
      date: (c: ExpenseClaim) => (c.date ? new Date(c.date as string) : null),
      client: (c: ExpenseClaim) => c.client,
      category: (c: ExpenseClaim) => c.category,
      amount: (c: ExpenseClaim) => Number(c.amount ?? 0),
      status: (c: ExpenseClaim) => c.status || 'requested'
    }),
    []
  );
  const { sorted, sort, toggle } = useTableSort(filtered, SORT_ACCESSORS, {
    key: 'date',
    dir: 'desc'
  });

  const awaiting = useMemo(
    () => claims.filter((c) => (c.status || 'requested') === 'requested').length,
    [claims]
  );
  const filteredTotal = useMemo(
    () => sorted.reduce((sum, c) => sum + Number(c.amount ?? 0), 0),
    [sorted]
  );

  const subLine = () => {
    if (!loaded && claims.length === 0) return 'Loading…';
    return [
      filtered.length === claims.length
        ? `${claims.length} claim${claims.length === 1 ? '' : 's'}`
        : `${filtered.length} of ${claims.length} claims`,
      formatZar(filteredTotal) !== '—' ? `${formatZar(filteredTotal)} total` : undefined,
      awaiting > 0 ? `${awaiting} awaiting approval` : undefined
    ]
      .filter(Boolean)
      .join(' · ');
  };

  return (
    <>
      <Topbar
        crumbs={['Human Resources', 'Expense claims']}
        tools={
          <button
            className="btn ghost"
            type="button"
            onClick={handleExport}
            disabled={exporting || claims.length === 0}
            title={exporting ? 'Preparing spreadsheet…' : 'Download all expense claims'}
          >
            <Icon name="download" size={14} />
            {exporting ? 'Exporting…' : 'Export'}
          </button>
        }
      />
      <div className="page">
        <div className="page-header">
          <div>
            <div className="eyebrow">Human Resources</div>
            <h1 className="page-title">Expense claims</h1>
            <div className="page-sub">{subLine()}</div>
          </div>
          <div className="page-tools">
            <label className="search">
              <Icon name="search" size={14} />
              <input
                type="text"
                placeholder="Search claims…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </label>
            {/* Filing a claim is self-service — every employee may do it.
                Opens the legacy form; the v3 form is not ported yet. */}
            <button
              className="btn primary"
              type="button"
              onClick={() => navigate('/human-resources/expense-claims/add')}
              title="Opens the legacy claim form"
            >
              <Icon name="plus" size={14} />
              New claim
            </button>
          </div>
        </div>

        <FilterChipStrip<StatusKey>
          options={STATUS_OPTIONS}
          value={statusFilter}
          onChange={setStatusFilter}
        />

        {sorted.length === 0 ? (
          <div className="card" style={{ padding: 48, textAlign: 'center' }}>
            <span className="muted">
              {!loaded && claims.length === 0
                ? 'Loading claims…'
                : claims.length === 0
                  ? 'No expense claims yet.'
                  : 'No claims match these filters.'}
            </span>
          </div>
        ) : (
          <div className="card flush">
            <table className="tbl">
              <thead>
                <tr>
                  {isHrPrivileged && (
                    <SortableTh label="Employee" sortKey="employee" sort={sort} onToggle={toggle} />
                  )}
                  <SortableTh label="Date" sortKey="date" sort={sort} onToggle={toggle} />
                  <SortableTh label="Client" sortKey="client" sort={sort} onToggle={toggle} />
                  <SortableTh label="Category" sortKey="category" sort={sort} onToggle={toggle} />
                  <SortableTh
                    label="Amount"
                    sortKey="amount"
                    sort={sort}
                    onToggle={toggle}
                    className="right"
                  />
                  <SortableTh label="Status" sortKey="status" sort={sort} onToggle={toggle} />
                  {isHrPrivileged && <th className="actions-cell" />}
                </tr>
              </thead>
              <tbody>
                {sorted.map((c) => {
                  const status = c.status || 'requested';
                  const busy = savingId === c.id;
                  return (
                    <tr key={c.id}>
                      {isHrPrivileged && <td className="strong">{c.employee || '—'}</td>}
                      <td>{periodLabel(c)}</td>
                      <td>{c.client || '—'}</td>
                      <td>{c.category ? c.category.replace(/_/g, ' ') : '—'}</td>
                      <td className="num right strong">{formatZar(c.amount)}</td>
                      <td>
                        <span className={`chip ${STATUS_TONE[status] || 'muted'}`}>
                          {STATUS_LABEL[status] || status}
                        </span>
                      </td>
                      {isHrPrivileged && (
                        <td className="actions-cell">
                          {status === 'requested' ? (
                            <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                              <button
                                className="btn ghost"
                                type="button"
                                disabled={busy}
                                onClick={() => setStatus(c, 'denied')}
                              >
                                Deny
                              </button>
                              <button
                                className="btn primary"
                                type="button"
                                disabled={busy}
                                onClick={() => setStatus(c, 'approved')}
                              >
                                {busy ? 'Saving…' : 'Approve'}
                              </button>
                            </div>
                          ) : (
                            // Already decided — show who signed it off rather
                            // than offering the buttons again.
                            <span className="muted" style={{ fontSize: 12 }}>
                              {c.approved_by || ''}
                            </span>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
};

export default ExpenseClaimsListV3;
